import Decimal from 'decimal.js';
import { useMemo } from 'react';

import { validateLiabilitySuggestion } from '@/modules/aiSuggestionReviewCard/AISuggestionReviewCard.utils';
import { AISuggestionReviewColumnData } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewCell';
import { AISuggestionReviewRow } from '@/modules/aiSuggestionReviewCard/components/AISuggestionReviewRow';
import { useAISuggestionReviewCardContext } from '@/modules/aiSuggestionReviewCard/context/aiSuggestionReviewCard.context';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_DISPLAY } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import { formatCurrency } from '@/utils/formatting/currency';

interface AISuggestionReviewLiabilityRowProps {
  suggestion: AiSuggestionForReviewCard;
}

export function AISuggestionReviewLiabilityRow({
  suggestion,
}: AISuggestionReviewLiabilityRowProps) {
  const { onReviewClick, onDeleteClick, onCreateClick } =
    useAISuggestionReviewCardContext();
  const { entities } = useHouseholdDetailsContext();

  const { columnsData, hasWarning } = useMemo<{
    columnsData: AISuggestionReviewColumnData[];
    hasWarning: boolean;
  }>(() => {
    const s = suggestion.liability;
    if (!s) return { columnsData: [], hasWarning: false };

    const {
      nameMissing,
      balanceMissing,
      counterpartyNeedsWarning,
      hasWarning,
    } = validateLiabilitySuggestion(suggestion, entities || []);

    const nameText = s.liabilityName || '';

    // Format the outstanding balance if it exists
    const balanceText = s.outstandingBalance
      ? formatCurrency(new Decimal(s.outstandingBalance))
      : '';

    // Determine counterparty display using debtor-first logic (same for V1 and V2)
    // If debtor exists, owner is debtor and counterparty is lender; otherwise vice versa
    const assignedByDebtor = !!s.debtorEntityID;
    const counterpartyLabel = assignedByDebtor ? 'Lender' : 'Debtor';
    const counterpartyID = assignedByDebtor
      ? s.lenderEntityID
      : s.debtorEntityID;
    const counterpartyName = assignedByDebtor
      ? s.lenderEntityName
      : s.debtorEntityName;

    const counterpartyText = counterpartyID
      ? counterpartyName || '-'
      : NO_LIABILITY_SOURCE_RECIPIENT_VALUE_DISPLAY;

    const columns: AISuggestionReviewColumnData[] = [
      {
        label: 'Name',
        values: [
          {
            displayText: nameText || '-',
            showWarning: nameMissing,
            warningTooltip: nameMissing
              ? 'Missing liability name. Review required.'
              : undefined,
          },
        ],
      },
      {
        label: counterpartyLabel,
        values: [
          {
            displayText: counterpartyText,
            showWarning: counterpartyNeedsWarning,
            warningTooltip: counterpartyNeedsWarning
              ? 'Entity not resolved. Review required.'
              : undefined,
          },
        ],
      },
      {
        label: 'Outstanding balance',
        values: [
          {
            displayText: balanceText || '-',
            showWarning: balanceMissing,
            warningTooltip: balanceMissing
              ? 'Missing outstanding balance. Review required.'
              : undefined,
          },
        ],
      },
    ];

    return { columnsData: columns, hasWarning };
  }, [entities, suggestion]);

  if (!suggestion.liability) return null;

  return (
    <AISuggestionReviewRow
      suggestionID={suggestion.id}
      rowHasWarning={hasWarning}
      columnsData={columnsData}
      onCreateClick={onCreateClick}
      onReviewClick={onReviewClick}
      onDeleteClick={onDeleteClick}
      // Align with 3 info columns (Name, Counterparty, Outstanding Balance)
      columnCount={3}
    />
  );
}
